import { api } from ".";
import {
  IClaim,
  IClaimResponse,
  IClaimFilters,
  IAIAnalysisResult,
  IDifferenceAnalysisResult,
  IUnderpaymentAnalysisPrompt,
  ISimilarClaimsResponse,
  ResolutionStatus,
} from "../../types/claim";

/**
 * RTK Query API slice for claims endpoints
 * Provides hooks for fetching claim data with filtering, sorting, and pagination
 */
const claimQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getClaims: builder.query<IClaimResponse, IClaimFilters>({
      query: (filters) => {
        // Build query parameters
        const params: Record<string, string> = {
          accountId: filters.accountId,
        };

        if (filters.search) {
          params.search = filters.search;
        }

        if (filters.startDate) {
          [params.startDate] = filters.startDate.toISOString().split("T");
        }

        if (filters.endDate) {
          [params.endDate] = filters.endDate.toISOString().split("T");
        }

        if (filters.sortField) {
          params.sortField = filters.sortField;
        }

        if (filters.sortOrder) {
          params.sortOrder = filters.sortOrder;
        }

        if (filters.page) {
          params.page = filters.page.toString();
        }

        if (filters.pageSize) {
          params.pageSize = filters.pageSize.toString();
        }

        if (filters.chargeBelowContractRateFilter) {
          params.chargeBelowContractRateFilter =
            filters.chargeBelowContractRateFilter;
        }

        if (filters.minDifferencePercent !== undefined) {
          params.minDifferencePercent = filters.minDifferencePercent.toString();
        }

        if (filters.categories && filters.categories.length > 0) {
          params.categories = filters.categories.join(",");
        }

        if (filters.underpaymentOnly !== undefined) {
          params.underpaymentOnly = filters.underpaymentOnly.toString();
        }

        if (filters.paymentTypes && filters.paymentTypes.length > 0) {
          params.paymentTypes = filters.paymentTypes.join(",");
        }

        if (filters.resolutionFilter) {
          params.resolutionFilter = filters.resolutionFilter;
        }

        if (filters.classificationFilter) {
          params.classificationFilter = filters.classificationFilter;
        }

        if (filters.clearPayerFilter) {
          params.clearPayerFilter = filters.clearPayerFilter;
        }

        if (filters.patientClassFilter) {
          params.patientClassFilter = filters.patientClassFilter;
        }

        return {
          url: "/claims",
          params,
        };
      },
      transformResponse: (response: any): IClaimResponse => {
        // Transform ISO date strings to Date objects
        const data: IClaim[] = response.data.map((item: any) => ({
          ...item,
          patientDOB: item.patientDOB ? new Date(item.patientDOB) : null,
          serviceDate: new Date(item.serviceDate),
          ediConversionFile: item.ediConversionFile
            ? {
                ...item.ediConversionFile,
                createdAt: new Date(item.ediConversionFile.createdAt),
              }
            : undefined,
        }));

        return {
          data,
          totalCount: response.totalCount,
          page: response.page,
          pageSize: response.pageSize,
          message: response.message,
        };
      },
      providesTags: ["Claims"],
    }),

    exportClaims: builder.query<
      { downloadUrl: string },
      Omit<IClaimFilters, "page" | "pageSize">
    >({
      query: (filters) => {
        // Build query parameters (same as getClaims but without pagination)
        const params: Record<string, string> = {
          accountId: filters.accountId,
        };

        if (filters.search) {
          params.search = filters.search;
        }

        if (filters.startDate) {
          [params.startDate] = filters.startDate.toISOString().split("T");
        }

        if (filters.endDate) {
          [params.endDate] = filters.endDate.toISOString().split("T");
        }

        if (filters.sortField) {
          params.sortField = filters.sortField;
        }

        if (filters.sortOrder) {
          params.sortOrder = filters.sortOrder;
        }

        if (filters.chargeBelowContractRateFilter) {
          params.chargeBelowContractRateFilter =
            filters.chargeBelowContractRateFilter;
        }

        if (filters.minDifferencePercent !== undefined) {
          params.minDifferencePercent = filters.minDifferencePercent.toString();
        }

        if (filters.categories && filters.categories.length > 0) {
          params.categories = filters.categories.join(",");
        }

        if (filters.underpaymentOnly !== undefined) {
          params.underpaymentOnly = filters.underpaymentOnly.toString();
        }

        if (filters.paymentTypes && filters.paymentTypes.length > 0) {
          params.paymentTypes = filters.paymentTypes.join(",");
        }

        if (filters.resolutionFilter) {
          params.resolutionFilter = filters.resolutionFilter;
        }

        if (filters.classificationFilter) {
          params.classificationFilter = filters.classificationFilter;
        }

        if (filters.clearPayerFilter) {
          params.clearPayerFilter = filters.clearPayerFilter;
        }

        if (filters.patientClassFilter) {
          params.patientClassFilter = filters.patientClassFilter;
        }

        return {
          url: "/claims/export",
          params,
        };
      },
    }),

    analyzeClaim: builder.mutation<
      { analysis: IAIAnalysisResult },
      { claimId: string; regenerate?: boolean }
    >({
      query: ({ claimId, regenerate = false }) => ({
        url: `/claims/${claimId}/analyze`,
        method: "POST",
        body: { regenerate },
      }),
      // Invalidate claims cache to trigger refetch with new aiAnalysisResult
      invalidatesTags: ["Claims"],
    }),

    /**
     * Analyze differences between underpaid claim and similar correctly-paid claims
     * Returns plain text AI analysis
     */
    analyzeDifferences: builder.mutation<
      IDifferenceAnalysisResult,
      { claimId: string; regenerate?: boolean }
    >({
      query: ({ claimId, regenerate = false }) => ({
        url: `/claims/${claimId}/analyze-differences`,
        method: "POST",
        body: { regenerate },
      }),
    }),

    /**
     * Update resolution status
     * Updates the resolution status of a claim with audit trail
     */
    updateResolutionStatus: builder.mutation<
      IClaim,
      { claimId: string; status: ResolutionStatus; notes?: string }
    >({
      query: ({ claimId, status, notes }) => ({
        url: `/claims/${claimId}/resolution-status`,
        method: "PATCH",
        body: { status, notes },
      }),
      // Invalidate claims cache to trigger refetch with updated status
      invalidatesTags: ["Claims"],
    }),

    /**
     * Get active prompt version
     * Retrieves the currently active AI prompt version (most recent)
     * SuperAdmin-only endpoint
     * Note: Endpoint path stays as /underpayment-analysis-prompts (table not renamed)
     */
    getActivePrompt: builder.query<IUnderpaymentAnalysisPrompt, void>({
      query: () => ({
        url: "/underpayment-analysis-prompts/active",
      }),
      providesTags: ["PromptVersions"],
    }),

    /**
     * Create new prompt version
     * Creates a new prompt version with auto-incremented version number
     * SuperAdmin-only endpoint
     * Note: Endpoint path stays as /underpayment-analysis-prompts (table not renamed)
     */
    createPromptVersion: builder.mutation<
      IUnderpaymentAnalysisPrompt,
      {
        identity: string;
        issueSummary: string;
        classification: string;
        appealLetter: string;
        finalRules: string;
      }
    >({
      query: (data) => ({
        url: "/underpayment-analysis-prompts",
        method: "POST",
        body: data,
      }),
      // Invalidate prompt versions cache to trigger refetch
      invalidatesTags: ["PromptVersions"],
    }),

    /**
     * Get all prompt versions
     * Retrieves all prompt versions for viewing history
     * SuperAdmin-only endpoint
     * Note: Endpoint path stays as /underpayment-analysis-prompts (table not renamed)
     */
    getAllPromptVersions: builder.query<IUnderpaymentAnalysisPrompt[], void>({
      query: () => ({
        url: "/underpayment-analysis-prompts",
      }),
      providesTags: ["PromptVersions"],
    }),

    /**
     * Get distinct AI classifications
     * Retrieves unique AI classification codes from analyzed claims for an account
     * Used to populate the classification filter dropdown dynamically
     */
    getDistinctClassifications: builder.query<
      { code: string; title: string }[],
      { accountId: string }
    >({
      query: ({ accountId }) => ({
        url: "/claims/classifications",
        params: { accountId },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Get distinct Clear payers
     * Retrieves unique Clear payer names from claims for an account
     * Used to populate the Clear Payer filter dropdown
     */
    getDistinctClearPayers: builder.query<string[], { accountId: string }>({
      query: ({ accountId }) => ({
        url: "/claims/clear-payers",
        params: { accountId },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Get similar correctly-paid claims
     * Finds claims from the same payer with overlapping "problem" CPT codes that were paid correctly
     * Requires AI analysis with underpaid line items
     * Used as evidence for appeals
     */
    getSimilarCorrectClaims: builder.query<
      ISimilarClaimsResponse,
      { claimId: string }
    >({
      query: ({ claimId }) => ({
        url: `/claims/${claimId}/similar-correct`,
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Reprocess a single claim from its original EDI file
     * Re-parses with current payer mappings and fee schedules
     */
    reprocessClaim: builder.mutation<{ data: IClaim; message: string }, string>(
      {
        query: (claimId) => ({
          url: `/claims/${claimId}/reprocess`,
          method: "POST",
        }),
        invalidatesTags: ["Claims"],
      }
    ),

    /**
     * Get a single claim by ID
     * Retrieves full claim details including line items
     * Used for navigation between claims in the modal
     */
    getClaimById: builder.query<IClaim, { claimId: string }>({
      query: ({ claimId }) => ({
        url: `/claims/${claimId}`,
      }),
      transformResponse: (response: any): IClaim => {
        // Transform ISO date strings to Date objects
        return {
          ...response,
          patientDOB: response.patientDOB
            ? new Date(response.patientDOB)
            : null,
          serviceDate: new Date(response.serviceDate),
          ediConversionFile: response.ediConversionFile
            ? {
                ...response.ediConversionFile,
                createdAt: new Date(response.ediConversionFile.createdAt),
              }
            : undefined,
        };
      },
      providesTags: ["Claims"],
    }),

    /**
     * Check if NCCI bypass is available for a claim
     * Returns whether the claim has an NCCI edit that can be bypassed
     */
    checkNcciBypassAvailable: builder.query<
      { data: { ncciBypassAvailable: boolean }; message: string },
      { claimId: string }
    >({
      query: ({ claimId }) => ({
        url: `/claims/${claimId}/ncci-bypass/available`,
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Toggle NCCI bypass for a claim
     * Enables or disables NCCI bypass and returns updated claim data
     */
    toggleNcciBypass: builder.mutation<
      {
        data: {
          claim: IClaim;
          bypassAmountInCents: number;
          bypassedCodes: string[];
          previousExpectedAmount: number;
          newExpectedAmount: number;
          newAmountDifference: number;
          newPaymentType: string;
        };
        message: string;
      },
      { claimId: string; enabled: boolean }
    >({
      query: ({ claimId, enabled }) => ({
        url: `/claims/${claimId}/ncci-bypass`,
        method: "PATCH",
        body: { enabled },
      }),
      invalidatesTags: ["Claims"],
    }),
  }),
});

export const {
  useGetClaimsQuery,
  useLazyExportClaimsQuery,
  useAnalyzeClaimMutation,
  useAnalyzeDifferencesMutation,
  useUpdateResolutionStatusMutation,
  useGetActivePromptQuery,
  useCreatePromptVersionMutation,
  useGetAllPromptVersionsQuery,
  useGetDistinctClassificationsQuery,
  useGetDistinctClearPayersQuery,
  useGetSimilarCorrectClaimsQuery,
  useReprocessClaimMutation,
  useGetClaimByIdQuery,
  useCheckNcciBypassAvailableQuery,
  useToggleNcciBypassMutation,
} = claimQueries;
